// frontend/app/(nurse)/applications/page.tsx
"use client";

import { useState } from "react";
import Link from "next/link";
import {
  Briefcase,
  Calendar,
  Clock,
  ExternalLink,
  Loader2,
  AlertCircle,
  CheckCircle2,
  XCircle,
  FileText,
  RefreshCw,
} from "lucide-react";
import { cn } from "@/lib/utils";
import {
  useNurseApplications,
  ApplicationStatus,
  ApplicationWithCompliance,
} from "@/lib/hooks/useNurseApplications";
import { useWithdrawApplication } from "@/lib/hooks/useWithdrawApplication";
import { NotificationsBell } from "@/components/notifications";
import { useToast } from "@/components/providers/ToastProvider";

// =============================================================================
// Types
// =============================================================================

type StatusFilter = ApplicationStatus | "ALL" | "ACTIVE";

// =============================================================================
// Status Badge Component
// =============================================================================

function StatusBadge({ status }: { status: ApplicationStatus }) {
  const config = {
    APPLIED: {
      bg: "bg-blue-100",
      text: "text-blue-700",
      icon: FileText,
      label: "Applied",
    },
    UNDER_REVIEW: {
      bg: "bg-purple-100",
      text: "text-purple-700",
      icon: Clock,
      label: "Under Review",
    },
    INTERVIEW: {
      bg: "bg-amber-100",
      text: "text-amber-700",
      icon: AlertCircle,
      label: "Interview",
    },
    OFFERED: {
      bg: "bg-emerald-100",
      text: "text-emerald-700",
      icon: CheckCircle2,
      label: "Offer Received",
    },
    HIRED: {
      bg: "bg-emerald-100",
      text: "text-emerald-700",
      icon: CheckCircle2,
      label: "Hired",
    },
    REJECTED: {
      bg: "bg-red-100",
      text: "text-red-700",
      icon: XCircle,
      label: "Not Selected",
    },
    WITHDRAWN: {
      bg: "bg-slate-100",
      text: "text-slate-600",
      icon: XCircle,
      label: "Withdrawn",
    },
  };

  const { bg, text, icon: Icon, label } = config[status] || config.APPLIED;

  return (
    <span
      className={cn(
        "inline-flex items-center gap-1.5 rounded-full px-2.5 py-0.5 text-xs font-medium",
        bg,
        text
      )}
    >
      <Icon className="h-3.5 w-3.5" />
      {label}
    </span>
  );
}

// =============================================================================
// Status Tabs Component
// =============================================================================

interface StatusTabsProps {
  selected: StatusFilter;
  onChange: (status: StatusFilter) => void;
  counts: Record<string, number>;
}

function StatusTabs({ selected, onChange, counts }: StatusTabsProps) {
  const tabs: { value: StatusFilter; label: string }[] = [
    { value: "ALL", label: "All" },
    { value: "ACTIVE", label: "Active" },
    { value: "APPLIED", label: "Applied" },
    { value: "UNDER_REVIEW", label: "Reviewing" },
    { value: "INTERVIEW", label: "Interview" },
    { value: "OFFERED", label: "Offered" },
    { value: "HIRED", label: "Hired" },
    { value: "REJECTED", label: "Rejected" },
    { value: "WITHDRAWN", label: "Withdrawn" },
  ];

  return (
    <div className="flex flex-wrap items-center gap-2">
      {tabs.map((tab) => {
        const count = tab.value === "ALL" ? counts.total : counts[tab.value] || 0;
        return (
          <button
            key={tab.value}
            type="button"
            onClick={() => onChange(tab.value)}
            className={cn(
              "rounded-full border px-3 py-1.5 text-xs font-medium transition-colors",
              selected === tab.value
                ? "border-emerald-600 bg-emerald-600 text-white"
                : "border-slate-200 bg-white text-slate-700 hover:bg-slate-50"
            )}
          >
            {tab.label}
            {count > 0 && (
              <span className={cn("ml-1.5", selected === tab.value ? "text-emerald-100" : "text-slate-500")}>
                ({count})
              </span>
            )}
          </button>
        );
      })}
    </div>
  );
}

// =============================================================================
// Application Card Component
// =============================================================================

interface ApplicationCardProps {
  application: ApplicationWithCompliance;
  onWithdraw: (id: string) => void;
  isWithdrawing: boolean;
}

function ApplicationCard({ application, onWithdraw, isWithdrawing }: ApplicationCardProps) {
  const canWithdraw =
    application.status === "APPLIED" ||
    application.status === "UNDER_REVIEW" ||
    application.status === "INTERVIEW";

  const canAccept = application.status === "OFFERED";

  return (
    <article className="rounded-lg border border-slate-200 bg-white p-5 transition-shadow hover:shadow-md">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        {/* Application Info */}
        <div className="min-w-0 flex-1">
          <div className="flex flex-wrap items-center gap-2">
            <Link
              href={`/applications/${application.id}`}
              className="text-lg font-semibold text-slate-900 hover:text-emerald-600 transition-colors"
            >
              {application.job_title}
            </Link>
            <StatusBadge status={application.status} />
          </div>

          <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-slate-600">
            <span className="flex items-center gap-1">
              <Briefcase className="h-4 w-4 text-slate-400" />
              {application.facility_name || "Healthcare Facility"}
            </span>
            <span className="flex items-center gap-1">
              <Calendar className="h-4 w-4 text-slate-400" />
              Applied {new Date().toLocaleDateString()}
            </span>
          </div>

          {/* Compliance Status */}
          {application.complianceStatus && (
            <div className="mt-3">
              {application.complianceStatus === "COMPLIANT" ? (
                <span className="inline-flex items-center gap-1 text-xs text-emerald-700">
                  <CheckCircle2 className="h-3.5 w-3.5" />
                  Credentials verified
                </span>
              ) : application.complianceStatus === "AT_RISK" ? (
                <span className="inline-flex items-center gap-1 text-xs text-amber-700">
                  <AlertCircle className="h-3.5 w-3.5" />
                  Some credentials pending
                </span>
              ) : (
                <span className="inline-flex items-center gap-1 text-xs text-red-700">
                  <XCircle className="h-3.5 w-3.5" />
                  Compliance issues detected
                </span>
              )}
            </div>
          )}
        </div>

        {/* Actions */}
        <div className="flex flex-col items-end gap-2">
          <Link
            href={`/applications/${application.id}`}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-4 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50 transition-colors"
          >
            View Details
            <ExternalLink className="h-3.5 w-3.5" />
          </Link>

          {canAccept && (
            <Link
              href={`/applications/${application.id}#accept-offer`}
              className="inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-4 py-2 text-sm font-semibold text-white hover:bg-emerald-500 transition-colors"
            >
              <CheckCircle2 className="h-4 w-4" />
              Review Offer
            </Link>
          )}

          {canWithdraw && (
            <button
              type="button"
              onClick={() => {
                if (confirm("Are you sure you want to withdraw this application?")) {
                  onWithdraw(application.id);
                }
              }}
              disabled={isWithdrawing}
              className="inline-flex items-center gap-2 rounded-lg border border-red-200 bg-white px-4 py-2 text-sm font-medium text-red-700 hover:bg-red-50 transition-colors disabled:opacity-50"
            >
              {isWithdrawing ? (
                <>
                  <Loader2 className="h-3.5 w-3.5 animate-spin" />
                  Withdrawing...
                </>
              ) : (
                <>
                  <XCircle className="h-3.5 w-3.5" />
                  Withdraw
                </>
              )}
            </button>
          )}
        </div>
      </div>
    </article>
  );
}

// =============================================================================
// Main Applications Page
// =============================================================================

export default function NurseApplicationsPage() {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>("ALL");
  const [page, setPage] = useState(1);
  const { showToast } = useToast();

  // Determine the actual status filter for the API
  let apiStatusFilter: ApplicationStatus | undefined = undefined;
  if (statusFilter !== "ALL" && statusFilter !== "ACTIVE") {
    apiStatusFilter = statusFilter;
  }

  // Fetch applications
  const { applications, total: _total, isLoading, error, refetch } = useNurseApplications({
    statusFilter: apiStatusFilter,
    page,
    pageSize: 20,
    includeCompliance: false, // We'll skip compliance for the list view
  });

  const withdrawMutation = useWithdrawApplication();

  // Filter for "ACTIVE" tab (client-side)
  let filteredApplications = applications;
  if (statusFilter === "ACTIVE") {
    filteredApplications = applications.filter((app) =>
      ["APPLIED", "UNDER_REVIEW", "INTERVIEW", "OFFERED"].includes(app.status)
    );
  }

  // Compute counts for tabs
  const counts = applications.reduce(
    (acc, app) => {
      acc[app.status] = (acc[app.status] || 0) + 1;
      if (["APPLIED", "UNDER_REVIEW", "INTERVIEW", "OFFERED"].includes(app.status)) {
        acc.ACTIVE = (acc.ACTIVE || 0) + 1;
      }
      return acc;
    },
    { total: applications.length, ACTIVE: 0 } as Record<string, number>
  );

  // Handlers
  const handleWithdraw = async (applicationId: string) => {
    try {
      await withdrawMutation.mutateAsync({ applicationId });
      showToast({
        variant: "success",
        title: "Application withdrawn",
        description: "Your application has been successfully withdrawn.",
      });
      refetch();
    } catch (err) {
      console.error("Failed to withdraw:", err);
      showToast({
        variant: "error",
        title: "Failed to withdraw",
        description: "An error occurred. Please try again.",
      });
    }
  };

  const handleStatusFilterChange = (status: StatusFilter) => {
    setStatusFilter(status);
    setPage(1);
  };

  return (
    <div className="mx-auto max-w-6xl px-4 py-8 sm:px-6 lg:px-8">
      {/* Header */}
      <header className="flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div>
          <h1 className="text-3xl font-bold text-slate-900">My Applications</h1>
          <p className="mt-2 text-sm text-slate-600">
            Track your job applications and manage offers.
          </p>
        </div>
        <div className="flex items-center gap-3">
          <NotificationsBell />
          <button
            type="button"
            onClick={() => refetch()}
            className="inline-flex items-center gap-2 rounded-lg border border-slate-200 bg-white px-3 py-2 text-sm font-medium text-slate-700 hover:bg-slate-50"
          >
            <RefreshCw className="h-4 w-4" />
            Refresh
          </button>
        </div>
      </header>

      {/* Status Tabs */}
      <section className="mt-6 rounded-lg border border-slate-200 bg-white p-4">
        <StatusTabs
          selected={statusFilter}
          onChange={handleStatusFilterChange}
          counts={counts}
        />
      </section>

      {/* Applications List */}
      <section className="mt-6 space-y-4">
        {/* Loading State */}
        {isLoading ? (
          <div className="rounded-lg border border-slate-200 bg-white p-8 text-center">
            <Loader2 className="mx-auto h-8 w-8 animate-spin text-slate-400" />
            <p className="mt-2 text-sm text-slate-500">Loading applications...</p>
          </div>
        ) : error ? (
          /* Error State */
          <div className="rounded-lg border border-red-200 bg-red-50 p-8 text-center">
            <AlertCircle className="mx-auto h-10 w-10 text-red-600" />
            <h3 className="mt-4 text-base font-medium text-red-900">
              Failed to load applications
            </h3>
            <p className="mt-2 text-sm text-red-700">{error.message}</p>
            <button
              type="button"
              onClick={() => refetch()}
              className="mt-4 inline-flex items-center gap-2 rounded-lg bg-red-600 px-4 py-2 text-sm font-medium text-white hover:bg-red-500"
            >
              <RefreshCw className="h-4 w-4" />
              Try Again
            </button>
          </div>
        ) : filteredApplications.length === 0 ? (
          /* Empty State */
          <div className="rounded-lg border border-slate-200 bg-white p-12 text-center">
            <Briefcase className="mx-auto h-12 w-12 text-slate-300" />
            <h3 className="mt-4 text-base font-medium text-slate-900">
              No applications found
            </h3>
            <p className="mt-2 text-sm text-slate-500">
              {statusFilter === "ALL"
                ? "You haven't applied to any jobs yet."
                : `You don't have any ${statusFilter.toLowerCase().replace("_", " ")} applications.`}
            </p>
            <Link
              href="/jobs"
              className="mt-6 inline-flex items-center gap-2 rounded-lg bg-emerald-600 px-6 py-3 text-sm font-semibold text-white hover:bg-emerald-500 transition-colors"
            >
              Browse Jobs
              <ExternalLink className="h-4 w-4" />
            </Link>
          </div>
        ) : (
          /* Applications List */
          <div className="space-y-3">
            <div className="text-sm text-slate-600">
              Showing {filteredApplications.length} application
              {filteredApplications.length !== 1 ? "s" : ""}
            </div>
            {filteredApplications.map((application) => (
              <ApplicationCard
                key={application.id}
                application={application}
                onWithdraw={handleWithdraw}
                isWithdrawing={withdrawMutation.isPending}
              />
            ))}
          </div>
        )}
      </section>

      {/* Quick Stats */}
      {!isLoading && applications.length > 0 && (
        <section className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium text-slate-600">Total Applications</p>
            <p className="mt-1 text-2xl font-bold text-slate-900">{counts.total}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium text-slate-600">Active</p>
            <p className="mt-1 text-2xl font-bold text-blue-600">{counts.ACTIVE || 0}</p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium text-slate-600">Offers Received</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600">
              {counts.OFFERED || 0}
            </p>
          </div>
          <div className="rounded-lg border border-slate-200 bg-white p-4">
            <p className="text-xs font-medium text-slate-600">Hired</p>
            <p className="mt-1 text-2xl font-bold text-emerald-600">
              {counts.HIRED || 0}
            </p>
          </div>
        </section>
      )}
    </div>
  );
}
